import React from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import styles from './TransactionSimpleDetail.module.css';
import { useTranslation } from 'react-i18next';
import SimpleTagList from 'components/ui/tags/SimpleTagList';
import TransactionPriorityInput from './details/TransactionPriorityInput';
import { MockService, MockServiceMessaging } from 'types/rest/mockServiceTypes';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import {
  HttpTransaction,
  MessagingMappings,
  MsgTransaction,
  Transaction,
  TransactionType,
} from 'types/rest/assetCatalogTypes';
import MessagingSimpleViewTabs from './details/MessagingSimpleViewTabs';
import MessagingBindings from './details/MessagingBindings';
import _ from 'lodash';
import { updateMockServiceTransactionField } from '../../../store/transactions/transactionsActions';
import HttpTransactionSimpleTabs from './details/HttpTransactionSimpleTabs';

interface TransactionSimpleDetailProps {
  /**
   * Transaction to be shown.
   */
  transaction: Transaction;

  /**
   * Parent wrapper object.
   */
  context: MockService | MockServiceTemplate;

  /**
   * Indicates whether to show editable input field with priority in the detail.
   */
  showTxnPriority: boolean;
  disabled?: boolean;

  /**
   * Handler for change of the transaction.
   * @param transaction
   * @param afterSaveCallback
   */
  onUpdateTransaction?: (transaction: Transaction) => void;

  /** If true, a copy link to transaction url will be displayed */
  showTransactionLink: boolean;

  paneType?: 'LEFT' | 'RIGHT';

  /** Type of the transaction */
  transactionType?: TransactionType;
}

/**
 * Simple detail of a Transaction
 */
const TransactionSimpleDetail = (props: TransactionSimpleDetailProps) => {
  const { t } = useTranslation('transactions');
  const transaction: Transaction = props.transaction as Transaction;
  const showTxnPriority = props.showTxnPriority;

  const onUpdateTransactionHandler = (priority: number) => {
    const txn = { ...transaction };
    txn.priority = priority;
    if (props.onUpdateTransaction) {
      props.onUpdateTransaction(txn);
    }
  };

  const onUpdateMessagingMappingsHandler = (messagingMappings: MessagingMappings) => {
    updateMockServiceTransactionField(
      'messagingTransactionMappings',
      messagingMappings,
      transaction.id,
      props.context,
      props.transactionType,
    );
  };

  return (
    <div className="m-4 position-relative">
      <div className="ml-3">
        <Row>
          <Col className={styles.titleMain}>
            <Form.Label>{t('txn.name')}:</Form.Label>
          </Col>
          <Col className={styles.value}>
            <span id="qa-txn-simple-detail-name" title={transaction.name}>
              {transaction.name}
            </span>
          </Col>
        </Row>
        <Row>
          <Col className={styles.titleMain}>
            <Form.Label>{t('txn.service')}:</Form.Label>
          </Col>
          <Col className={styles.value}>
            <span id="qa-txn-simple-detail-service-name">{transaction.serviceName}</span>
          </Col>
        </Row>
        <Row>
          <Col className={styles.titleMain}>
            <Form.Label>{t('txn.description')}:</Form.Label>
          </Col>
          <Col className={styles.value}>
            <span id="qa-txn-simple-detail-descr">{transaction.description ? transaction.description : '-'}</span>
          </Col>
        </Row>
        <Row>
          <Col className={styles.titleMain}>
            <Form.Label>{t('txn.tags')}:</Form.Label>
          </Col>
          <Col className={styles.value}>
            <SimpleTagList tags={transaction.tags} />
          </Col>
        </Row>
        {showTxnPriority && (
          <TransactionPriorityInput
            disabled={props.disabled ? props.disabled : false}
            priority={transaction.priority}
            context={props.context}
            transactionId={transaction.id}
            onUpdateTransactionPriorityHandler={onUpdateTransactionHandler}
          />
        )}

        {props.transactionType === 'MESSAGING' && props.paneType === 'RIGHT' && (
          <MessagingBindings
            onUpdateBindings={onUpdateMessagingMappingsHandler}
            brokerConfig={(props.context as MockServiceMessaging).brokerConfig}
            transaction={props.transaction as MsgTransaction}
          />
        )}

        {props.transactionType === 'HTTP' ? (
          <HttpTransactionSimpleTabs
            context={props.context}
            transaction={props.transaction as HttpTransaction}
            showTransactionLink={props.showTransactionLink}
          />
        ) : (
          <MessagingSimpleViewTabs context={props.context} transaction={props.transaction as MsgTransaction} />
        )}
      </div>
    </div>
  );
};

export default TransactionSimpleDetail;
